import { StatusMetaData, StatusMetaDataCollection } from './status.metadata.js';
import { VisualizationType } from './VisualizationType.js';

// Stolen from jsonwebtoken 8.5
type Algorithm =
	| 'HS256'
	| 'HS384'
	| 'HS512'
	| 'RS256'
	| 'RS384'
	| 'RS512'
	| 'ES256'
	| 'ES384'
	| 'ES512'
	| 'PS256'
	| 'PS384'
	| 'PS512'
	| 'none';

export class DocumentGeneration {
	public documentDownloadEnabled: boolean;
	public verboseMode: boolean;
	public timestampFilenames: boolean;
	public maxCompanyCountAllowedForPdf: number;
}

export enum Environment {
	Development = 'Development',
	Integration = 'Integration',
	Staging = 'Staging',
	Production = 'Production',
}

export enum Mode {
	Company = 'Company',
	Asset = 'Asset',
}

export enum TabConfig {
	PUBLIC = 'PUBLIC',
	JNJ_ONLY = 'JNJ_ONLY',
	BLUE_KNIGHT = 'BLUE_KNIGHT',
	BLUE_KNIGHT_MILESTONES = 'BLUE_KNIGHT_MILESTONES',
	ASSET = 'ASSET',
	ASSETS = 'ASSETS',
}

export class Sector {
	public name = 'Unknown';
	public subsectors: string[] = [];
}

export class GeneralConfig {
	public instanceId: string;
	public defaultLocale: string;
	public appWidth: number;
	public behavior: Behavior;
	public email: EmailConfig;
	public environment: Environment;
	/**
	 * If provided, the elasticsearch connection will be created using this value rather than instanceId.
	 * Currently this is only needed in the universal-vanilla deployment where the instanceId for the database needs to be (jlabs/korea/singapore) but the ES instance is universal-vanilla.
	 */
	public elasticsearchInstanceId?: string;
	public documentGeneration: DocumentGeneration;
	public featureSwitches: FeatureSwitches;
	public themeSettings: ThemeSettings;
	public freeTextSearchTypeahead: FreeTextSearchTypeahead;
	public hubSpotIntegration: HubSpotIntegration;
	public juniverseIntegration: JuniverseIntegration;
	public jforceIntegration: JforceIntegration;
	public locationMetadata: LocationMetaData[];
	public progressIndicator: ProgressIndicator;
	public referenceValueData: ReferenceValueData;
	public sso: SsoConfig;
	public hostedUrl: string;
	public debug: DebugSettings = new DebugSettings();
	public gtmId: string;
	public responsive: ResponsiveConfig;
	public sectors: Sector[] = [];
	public statusesForTile: string[];
	public tooltipForTile: string;
	public statusForCompanyIcon: string;
	public tooltipForCompanyIcon: string;
	public companyStatusIcon: string;
	public rawStatusMetadata: StatusMetaData[];
	public statusMetadata: StatusMetaDataCollection;
	public specialDesignationOrder: number;
	public rateLimit: RateLimitConfig;

	constructor(initConfig: GeneralConfig) {
		Object.assign(this, initConfig);
		this.statusMetadata = new StatusMetaDataCollection(
			this.statusesForTile,
			this.rawStatusMetadata,
			this.specialDesignationOrder
		);
	}

	private static _instance: GeneralConfig;
	public static get globalInstance(): GeneralConfig {
		return GeneralConfig._instance;
	}
	public static set globalInstance(instance: GeneralConfig) {
		GeneralConfig._instance = new GeneralConfig(instance);
	}

	public get isProduction(): boolean {
		return this.environment === Environment.Production;
	}

	public get isDevelopment(): boolean {
		return this.environment === Environment.Development;
	}

	/** Returns the config-drvien metadata for a location.
	 * Note that this is merged with the db data at injest
	 * and should never be needed elsewhere, especially in
	 * front end.
	 */
	public getLocationMetadata(dbLocation: string): LocationMetaData {
		return this.locationMetadata.find((meta) =>
			meta.db.includes(dbLocation)
		);
	}
}

export class ResponsiveConfig {
	public followListBreakpoint: number;
}

export class DebugStatus {
	public status = '';
	public opportunityIds: string[] = [];
}

export class DebugSubsector {
	public subsector = '';
	public opportunityIds: string[] = [];
}

export class DebugSubsectors {
	public primarySubsectors: string[];
	public secondarySubsectors: string[];
}

export class EmailDebugSettings {
	/**  Enables the preview for nodemailer, see their documentation for details. */
	public debugSmtp: boolean = false;
	/** Enables the endpoints in email-debug.controller. Has no effect in production. */
	public debugEndpointEnabled: boolean = false;
	/** Append the UNIX timestamp to the filename when onlyRender is enabled. Otherwise subsequent usage of the template will overwrite the rendered HTML. */
	public includeTimestamp: boolean = false;
	/** If provided, all emails generated will also be copied to this address. */
	public cc?: string;
	/** Enables the preview for email-templates, see their documentation for details. */
	public preview: boolean = false;
	/** Do not send any emails. Instead, write the rendered HTML to disk. */
	public onlyRender: boolean = false;
	/** If provided in a non-production environment, all emails from the system will instead go to this address. */
	public lowerEnvironmentEmailAddressOverride?: string;
	/** The email address that the debug endpoint sends to. */
	public debugEmailAddress?: string;
	/** Ignores the timezone and time since last email check for the newsletter, instead generating an email for every user with preferences. */
	public sendAllUsersNewsletter: boolean = false;
}

export class CachingDebugSettings {
	public cacheDevRequests: boolean = false;
	public bypassServerCache: boolean = false;
	public verboseLogs: boolean = false;
	public bypassLocalizedDictCache: boolean = false;
}

export class JuniverseDebugSettings {
	public bypass: boolean = false;
	public autoSendBypass: boolean = false;
	public passFullConfigToClient: boolean = false;
}

export class DebugSettings {
	public showHealthInfo: boolean = false;
	public alwaysUseNarrowScreen: boolean = false;
	public applyDebugStatuses: boolean = false;
	public applyDebugSubsectors: boolean = false;
	public viewsToBypass: string = '';
	public debugStatuses: DebugStatus[] = [];
	public allowAllReloads: boolean = false;
	public applyOlderUpdates: boolean = false;
	public applyFailedUpdates: boolean = false;
	public applyUpdatesSince?: Date;
	public debugPrimarySubsectors: DebugSubsector[] = [];
	public debugSecondarySubsectors: DebugSubsector[] = [];
	public email: EmailDebugSettings = new EmailDebugSettings();
	public caching: CachingDebugSettings = new CachingDebugSettings();
	public juniverse: JuniverseDebugSettings = new JuniverseDebugSettings();
	public sendMagicLinkToBrowser: boolean = false;

	// FUTURE - it would be nice for these to be non-static methods,
	// but the constructor isn't loaded for all paths and it was
	// deemed too much effort to keep chasing for now
	public static getEnabledFlags(config: DebugSettings): string {
		const enabledFlags = [
			...(config.showHealthInfo ? ['showHealthInfo'] : []),
			...(config.alwaysUseNarrowScreen ? ['alwaysUseNarrowScreen'] : []),
			...(config.applyDebugSubsectors ? ['applyDebugSubsectors'] : []),
			...(config.viewsToBypass ? ['viewsToBypass'] : []),
			...(config.caching.bypassServerCache ? ['caching.bypass'] : []),
			...(config.caching.verboseLogs ? ['caching.verboseLogs'] : []),
			...(config.caching.bypassLocalizedDictCache
				? ['caching.bypassLocalizedDictCache']
				: []),
			...(config.email.debugSmtp ? ['email.debugSmtp'] : []),
			...(config.email.onlyRender ? ['email.onlyRender'] : []),
			...(config.email.sendAllUsersNewsletter
				? ['email.sendAllUsersNewsletter']
				: []),
			...(config.email.debugEndpointEnabled
				? ['email.debugEndpointEnabled']
				: []),
			...(config.juniverse.bypass ? ['juniverse.bypass'] : []),
			...(config.juniverse.autoSendBypass
				? ['juniverse.autoSendBypass']
				: []),
		];
		return enabledFlags.length > 0 ? enabledFlags.join() : `none`;
	}
}

export class PerPageModes {
	public explore: Mode[];
	public searchResults: Mode[];
	public companySpecific: Mode[];
	public assetSpecific: Mode[];
}

export class Behavior {
	public clearFilterUponNavigateToExplore: boolean;
	public drillDownNavigatesToSearchResults: boolean;
	public lastViewedMyUpdatesLimit?: number;
	public logoutSuccessToastTimeout?: number;
	public tagsPickerModalLaunchTimeout?: number;
	public virtualScrollToTopTimeout?: number;
	public subsectorsIndependentOnDropdown: boolean;
	public subsectorsPerColumn: number;
	public applyPreLaunchUpdates: boolean;
	public initialVisualization: VisualizationType;
	public defaultMode: Mode;
	public enabledModes: Mode[];
	public perPageModes: PerPageModes;
	public defaultTab: TabConfig;
}

export class CompanyUpdatePropertyRelationship {
	public key: string;
	public relatedProperties: string[];
}
export function getRelationshipByPropertyName(
	companyUpdatePropertyRelationships: CompanyUpdatePropertyRelationship[],
	propertyName: string
): CompanyUpdatePropertyRelationship {
	return companyUpdatePropertyRelationships.find(
		(cupr) =>
			cupr.key === propertyName ||
			cupr.relatedProperties.find((p) => p === propertyName)
	);
}

export class JuniverseIntegration {
	public enabled: boolean;
	public tokenEndpoint: string;
	public tokenIssuer: string;
	public tokenAlgorithms: Algorithm[];
	public keysForJWTs: string[];
	public tokenToSend: string;
	public companyOverrideToken: string;
	public internalOverrideToken: string;
	public internalSuperOverrideToken: string;
	public partnerOverrideToken: string;
	public bardaOverrideToken: string;
	public invalidOverrideToken: string;
	public juniverseUrl: string;
	public juniverseLogin: string;
	public paidUserRoles: string;
	public companyRoles: string;
	public bardaRoles: string;
	public internalRoles: string;
	public internalSuperRoles: string;
}

export class JforceIntegration {
	public jforceUrl: string;
}

export interface SmtpInfo {
	host: string;
	port: number;
	username: string;
	password: string;
	from: string;
	replyTo: string;
	// Not certain how a bunyan-compatible logger instance is passed,
	// but it is an option.  True means console, false means no logging.
	// For now, will only support boolean values.  If we ever want to
	// support Bunyan loggers, we can add that type here
	logger: boolean; // | any;
}

export class GeoPosition {
	public lat: number;
	public lng: number;
}

export class PublicOverrides {
	public currentRdStage = false;
	public investmentSummary = false;
	public deals = false;
	public funding = false;
	public companyContact = false;
	public leadProductStage = false;
}

export class CompanyUpdateSwitches {
	public enableDiversityQuestions: boolean;
	public enableSectorUpdates: boolean;
	public enableProblemAndSolution: boolean;
	public enableProgressUpdates: boolean;
	public enableKeyManagement: boolean;
	public enableFirstTimeEntrepreneur: boolean;
	public enableDeals: boolean;
	public enableTags: boolean;
}

export class FeatureSwitches {
	/** Collapses the application down to nothing but the Search Results page.
	 *  Every other Angular route redirects to /search-results, and the API only
	 *  serves the endpoints that the Search Results page (and the login flow it
	 *  depends on) needs. When false, the application behaves normally. */
	public restrictToSearchResultsPage: boolean;
	public enableLogin: boolean;
	public enableShowNewCompanies: boolean;
	public newCompaniesFallsBackToX: number;
	public enableByTheNumbers: boolean;
	public enableUserPrefs: boolean;
	public enableFollowCompanies: boolean;
	public enableSitemap: boolean;
	public enablePaywall: boolean;
	public enableQfc: boolean;
	/** Includes enhanced Blue Knight functionality (additional data fields, difference Company Self Update experience).
	 * NOTE: The Blue Knight status of a company is always visible, since that is an attribute of a company rather than a Blue Knight feature per say. */
	public enableBlueKnight: boolean;
	public enableNews: boolean;
	public enableSectors: boolean;
	public enableSecondarySectors: boolean;
	public enableLeadProductStage: boolean;
	public enableIndicationUpdates: boolean;
	public enableIndicationFilter: boolean;
	public enableJPAL: boolean;
	public enableAudienceCheck: boolean;
	public enableShowGeoLocationMap: boolean;
	public enableShowTags: boolean;
	public enableShowLocationStatus: boolean;
	public enableDealsWidget: boolean;
	public enableShowCompanyLogos: boolean;
	/** Controls whether locations show on the filters, company summaries and whether location statuses render.
	 *  This can be explicitly provided in the config, or it will be derived at startup based on whether multiple locations are defined.
	 * */
	public enableShowLocations?: boolean = null;
	/** Overlaps a bit with DocumentGeneration.documentDownloadEnabled, but not fully.
	 * Disabling this now supersedes that. */
	public enableDownload: boolean;
	public enableModeSwitching: boolean;
	public includeInPublicView: PublicOverrides;
	public companyDetailsSwitches: CompanyDetailsSwitches;
	public companyUpdateSwitches: CompanyUpdateSwitches;
	public enableMagicLink: boolean;
}

export class CompanyDetailsSwitches {
	public showSectorDetails: boolean;
	public showSiteHead: boolean;
	public showContactEmailAndPhone: boolean;
}

export class MapSettings {
	public apiKey: string;
	public styleKey: string;
	public aspectRatio: number;
	public maxWidth: string;
	public maxHeight: string;
	public centerLngLat?: [number, number];
	public initialBounds?: [[number, number], [number, number]];
	public maxBounds?: [[number, number], [number, number]];
	public initialZoomFactor?: number;
	public minZoomFactor?: number;
	public emptyRadius: number;
	public minRadius: number;
	public maxRadius: number;
	public radiusScaleFactor: number;
}
export class ThemeSettings {
	public logoImgSrc: string;
	public exportLogoPath: string;
	public faviconSrc: string;
	public stayInTheLoopImgSrc: string;
	public sectorColors: string[];
	public sectorContrastLabelColors: string[];
	public map: MapSettings = new MapSettings();
	public vars: { [key: string]: string } = {};
	public dropdownItemsPerColumn: number;
}

export class FreeTextSearchTypeahead {
	public debounceTimeMilliseconds: number;
	public matchAfterHowManyLetters: number;
	public numberOfSuggestionsToShow: number;
	public refocusTimeout: number;
}

export class HubSpotIntegration {
	public formId: string;
	public portalId: string;
}

/** All of the extra metatadata associated with a company.
 *  This is used for reanming locations, merging locations,
 *  and configuring datathat cannot be sourced from JFORCE */
export class LocationMetaData {
	/** A list of values from the database which all are treated as the
	 * same location  for Navigator */
	public db: string[];

	/** The name used everywhere in Navigator
	 * - including indexing, display, and storing
	 * transactional data */
	public name: string;

	/** Only used in By The Numbers - All locations with
	 *  the same aggregate name will be grouped together
	 *  in the By The Numbers location filter */
	public aggregateName?: string;

	/** The city the location is associated with, if applicable */
	public city: string;

	/** The latitude and longitude to place the location on a map */
	public geoPosition: GeoPosition;

	/** The order to show the location in the filter dropdown */
	public sortOrder: number;

	/** Determines if a location (e.g ENA/WNA) should always appear first in the geo visualization tooltip */
	public firstInGeoTooltip: boolean = false;

	/** @deprecated - Used to determine DEI numbers until
	 *  the widget cuts over to the new values */
	public isNorthAmerican: boolean;

	/** Prevents this location from rendering in the
	 *  geographic visualiztion */
	public excludeFromMap?: boolean;

	/** Changes how filtering on this location matches: instead of returning every
	 *  company associated with it, return only the companies associated with
	 *  nothing but it. Intended for catch-all locations like Virtual, where
	 *  "Virtual" is meaningful only when it is a company's sole location.
	 *  Currently only honored when the restrictToSearchResultsPage feature switch is on. */
	public matchExclusively?: boolean;
}

export class ProgressIndicator {
	public delay: number;
	public pauseForStaticIndicator: number;
}

export class RoundsBySector {
	[sector: string]: CurrentRdStage[];
}

export class ReferenceValuesBySector {
	[sector: string]: ReferenceValue[];
}

export type ReferenceValueTypes =
	| ReferenceValue[]
	| RoundsBySector
	| ReferenceValuesBySector;

export class ReferenceValueData {
	public rdStagesBySector: RoundsBySector;
	public dealConfidentialityList: DealConfidentiality[];
	public dealTypeList: DealType[];
	public fundingConfidentialityList: FundingConfidentiality[];
	public fundingCurrencyList: FundingCurrency[];
	public fundingRoundList: ReferenceValue[];
	public securedFundingCurrencyList: SecuredFundingCurrency[];
	public technologyReadinessLevelList: TechnologyReadinessLevel[];
	public countryForDeiReportingList: CountryForDeiReporting[];
	public deiCountriesAndOptionsList: DeiConfig[];
	public blueKnightGoals: ReferenceValue[];
	public teamSizeOptions: ReferenceValue[];
	public blueKnightFundingRounds: ReferenceValue[];
	public blueKnightPharmaStage: ReferenceValue[];
	public blueKnightMedTechStage: ReferenceValue[];
	public regulatoryStages: ReferenceValue[];
	public sectors: ReferenceValue[];
	public subsectors: ReferenceValuesBySector;
	public years: ReferenceValue[];
	public indications: ReferenceValue[];
	public therapeuticAreas: ReferenceValue[];
	public developmentStages: ReferenceValuesBySector;
	public focusAreas: ReferenceValuesBySector;
	public routeOfAdministration: ReferenceValue[];

	public currentPartneringStatus: ReferenceValue[];
	public medicalDeviceCategorization: ReferenceValue[];
	public regulatoryApprovalStatus: ReferenceValue[];
	public medicalDeviceFdaCategory: ReferenceValue[];
	public partnerRole: ReferenceValue[];
	public deliverableType: ReferenceValue[];
	public clinicalTrialCountries: ReferenceValue[];
	public keywords: ReferenceValue[];
}

export type ReferenceValueKey = keyof ReferenceValueData;

export class ReferenceValue {
	public isdeleted?: boolean = false;
	public label?: string;
	public sector?: string;
	public rowId?: number;
	public value: string;
}

export class SsoConfig {
	public acsUrl: string;
	public enabled: boolean;
	public entityID: string;
	public idpLoginUrl: string;
	public idpLogoutUrl: string;
	public idpSigningCertLocation: string;
	public isAuthenticatedAssertion: boolean;
}

export class ContingentFundingCurrency extends ReferenceValue {
	public contingentFundingCurrency: string;
}

// FUTURE: Clean up the typing here. I don't think we're getting much from them,
//         and we're not actually using the properties in each type, we're using
//         value & label instead.
export class CurrentRdStage extends ReferenceValue {
	public rank?: number;
	public sector?: string;
}

export class DealConfidentiality extends ReferenceValue {
	public dealConfidentiality: ConfidentialityType;
}

export class DealType extends ReferenceValue {
	public dealType: string;
}

export class FundingConfidentiality extends ReferenceValue {
	public fundingConfidentiality: ConfidentialityType;
}

export class FundingCurrency extends ReferenceValue {
	public fundingCurrency: string;
}

export class SecuredFundingCurrency extends ReferenceValue {
	public securedFundingCurrency: string;
}

export class TechnologyReadinessLevel extends ReferenceValue {}

export class CountryForDeiReporting extends ReferenceValue {}

export class DeiConfig extends ReferenceValue {
	public diversityOptions: DiversityOption[];
}

export class DiversityOption extends ReferenceValue {
	// FUTURE: Default values don't manifest when the config package reads in the default.json
	//  So, they must default to their typical uninitialized default values
	public omitCountryPrefix = false;
	public isMutuallyExclusive = false;
	public isOnlyForBoardAdvisor = false;
}

export enum ConfidentialityType {
	Confidential = 'Confidential',
	Public = 'Public',
}

export interface ContifyConfig {
	apiUrl: string;
	appId: string;
	appKey: string;
	maxNumberOfQueryItemsPerNewsChunk: number;
	newsLanguageId: string;
	apiMaxRetries: number;
	apiWaitTimeBetweenRetriesMillis: number;
	enableCompanyMatching: boolean;
	enableUpdateCompanyNews: boolean;
	enableExternalNewsCronSchedule: boolean;
	enableDetailedMappingLogs: boolean;
	externalNewsCronSchedule: string;
	signalLookbackInDays: number;
}

export interface EmailConfig {
	sendNewsletterEmailsCronSchedule: string;
	sendNewsletterEmailsHourOfDay: number;
	enableSendNewsletterEmails: boolean;
	enablePendingCompanyEditsEmails: boolean;
	sendPendingCompanyEditsEmailsCronSchedule: string;
	pendingCompanyEditsEmailsLookbackThresholdDays: number;
	weeksForEscalation: number;
	pendingCompanyEditsEscalationAddress: string;
	enableUpdateReminderEmails: boolean;
	allowRepeatUpdateRemindersForNonBKCompanies: boolean;
	sendRemindersToAlumni: boolean;
	sendUpdateReminderEmailsCronSchedule: string;
	sendNotificationToCoOwners: string;
}

export interface WebsiteConfig {
	baseUrl: string;
	unhostedUrl: string;
	hostingDomains: string;
}

export interface LocalizationDict {
	[key: string]: string;
}
/**
 * Defines the configuration for the rate limiter on any endpoints it is configured with,
 */
export interface RateLimitConfig {
	/** If false, no rate limiting will apply */
	enabled: boolean;
	/** The window length, in minutes, to limit requests over */
	window: number;
	/** How many requests can be made over the window before they are blocked. */
	max: number;
}
